/**
 * "Work authorisation": the customer's visa-sponsorship policy, as a default plus optional per-country
 * overrides (US and Netherlands first). It only changes how a work-authorisation flag READS for the hiring
 * team. It never changes the question Rio asks a candidate and never affects the Fit Score. Say so on the
 * page, so nobody sets it expecting it to filter candidates.
 *
 * A Super Admin may set any customer's; an Admin only their own company's (avrio-backend#225). Until #225 is
 * live an Admin's request is refused with 403: that is shown as a calm "not available yet" note, not an error.
 *
 * GET/PUT /api/customers/{id}/sponsorship-policy. The PUT REPLACES the whole policy, so overrides for
 * countries this page does not show are sent back unchanged: saving must never wipe them.
 */
import { memo, useCallback, useEffect, useState } from 'react';
import { Alert, Button, Radio, Space, Spin, Typography } from 'antd';
import axios from '../../../../axiosConfig';
import { CustomerSponsorshipPolicy, SponsorshipPolicyValue } from '../../types';
import styles from './index.module.css';

const { Title, Text } = Typography;

const COUNTRIES = [
  { code: 'US', name: 'United States' },
  { code: 'NL', name: 'Netherlands' },
];
const SHOWN_CODES = COUNTRIES.map((country) => country.code);

const POLICY_LABELS: Record<SponsorshipPolicyValue, string> = {
  SPONSORS: 'We sponsor',
  DOES_NOT_SPONSOR: 'We don’t sponsor',
  CASE_BY_CASE: 'Case by case',
};
const POLICIES = Object.keys(POLICY_LABELS) as SponsorshipPolicyValue[];

/** The radio value meaning "no choice here". */
const NONE = 'NONE';
type Choice = SponsorshipPolicyValue | typeof NONE;

interface Props {
  customerId: number;
  customerName: string;
}

const isPolicy = (value: unknown): value is SponsorshipPolicyValue =>
  typeof value === 'string' && value in POLICY_LABELS;

const SponsorshipPolicySection = ({ customerId, customerName }: Props) => {
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [notAllowed, setNotAllowed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [defaultChoice, setDefaultChoice] = useState<Choice>(NONE);
  const [countryChoice, setCountryChoice] = useState<Record<string, Choice>>(
    {},
  );
  // Overrides for countries this page does not show: kept and sent back untouched.
  const [otherOverrides, setOtherOverrides] = useState<
    CustomerSponsorshipPolicy['overrides']
  >([]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadFailed(false);
    setNotAllowed(false);
    setSaved(false);
    setSaveError(null);
    setDirty(false);
    axios
      .get<CustomerSponsorshipPolicy>(
        `/api/customers/${customerId}/sponsorship-policy`,
      )
      .then((res) => {
        if (cancelled) return;
        const policy = res.data;
        setDefaultChoice(
          isPolicy(policy?.defaultPolicy) ? policy.defaultPolicy : NONE,
        );
        const overrides = Array.isArray(policy?.overrides)
          ? policy.overrides
          : [];
        const next: Record<string, Choice> = {};
        SHOWN_CODES.forEach((code) => {
          const own = overrides.find((o) => o.countryCode === code);
          next[code] = own && isPolicy(own.policy) ? own.policy : NONE;
        });
        setCountryChoice(next);
        setOtherOverrides(
          overrides.filter((o) => !SHOWN_CODES.includes(o.countryCode)),
        );
      })
      .catch((err) => {
        if (cancelled) return;
        if (err?.response?.status === 403) setNotAllowed(true);
        else setLoadFailed(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [customerId, attempt]);

  const save = useCallback(() => {
    setSaving(true);
    setSaveError(null);
    setSaved(false);
    const body: CustomerSponsorshipPolicy = {
      ...(defaultChoice !== NONE ? { defaultPolicy: defaultChoice } : {}),
      overrides: [
        ...SHOWN_CODES.filter((code) => countryChoice[code] !== NONE).map(
          (code) => ({ countryCode: code, policy: countryChoice[code] }),
        ),
        ...otherOverrides,
      ],
    };
    axios
      .put<CustomerSponsorshipPolicy>(
        `/api/customers/${customerId}/sponsorship-policy`,
        body,
      )
      .then(() => {
        setSaved(true);
        setDirty(false);
      })
      .catch((err) => {
        const status = err?.response?.status;
        const message = err?.response?.data?.message;
        if (status === 403) {
          setSaveError(
            'You can’t change this setting yet. Ask Avrio to set it for you.',
          );
        } else {
          setSaveError(
            typeof message === 'string' && message.trim()
              ? `Could not save: ${message}`
              : 'Could not save. Try again.',
          );
        }
      })
      .finally(() => setSaving(false));
  }, [customerId, defaultChoice, countryChoice, otherOverrides]);

  const touch = () => {
    setDirty(true);
    setSaved(false);
  };

  return (
    <section
      className={styles.section}
      aria-labelledby="sponsorship-policy-title"
    >
      <Title level={5} id="sponsorship-policy-title" className={styles.title}>
        Work authorisation
      </Title>
      <Text type="secondary" className={styles.note}>
        Whether {customerName} sponsors visas. This only changes how a
        work-authorisation flag reads for your hiring team. It does not change
        the question candidates are asked and it never affects a Fit Score.
      </Text>

      {loading && <Spin />}

      {!loading && notAllowed && (
        <Alert
          type="info"
          showIcon
          message="This setting isn’t available to your account yet."
          description="Ask Avrio to set it for you."
        />
      )}

      {!loading && loadFailed && (
        <Alert
          type="error"
          showIcon
          message="Could not load the sponsorship policy."
          action={
            <Button size="small" onClick={() => setAttempt((n) => n + 1)}>
              Try again
            </Button>
          }
        />
      )}

      {!loading && !notAllowed && !loadFailed && (
        <Space direction="vertical" size={12} className={styles.body}>
          <div>
            <Text strong id="sponsorship-default-label">
              Default for every country
            </Text>
            <div>
              <div
                role="radiogroup"
                aria-labelledby="sponsorship-default-label"
              >
                <Radio.Group
                  value={defaultChoice}
                  onChange={(event) => {
                    setDefaultChoice(event.target.value as Choice);
                    touch();
                  }}
                  options={[
                    { value: NONE, label: 'Not set' },
                    ...POLICIES.map((value) => ({
                      value,
                      label: POLICY_LABELS[value],
                    })),
                  ]}
                />
              </div>
            </div>
          </div>

          {COUNTRIES.map((country) => (
            <div key={country.code}>
              <Text strong id={`sponsorship-${country.code}-label`}>
                {country.name}
              </Text>
              <div>
                <div
                  role="radiogroup"
                  aria-labelledby={`sponsorship-${country.code}-label`}
                >
                  <Radio.Group
                    value={countryChoice[country.code] ?? NONE}
                    onChange={(event) => {
                      setCountryChoice((previous) => ({
                        ...previous,
                        [country.code]: event.target.value as Choice,
                      }));
                      touch();
                    }}
                    options={[
                      { value: NONE, label: 'Use the default' },
                      ...POLICIES.map((value) => ({
                        value,
                        label: POLICY_LABELS[value],
                      })),
                    ]}
                  />
                </div>
              </div>
            </div>
          ))}

          {saved && <Alert type="success" showIcon message="Saved" />}
          {saveError && <Alert type="error" showIcon message={saveError} />}
          <Space>
            <Button
              type="primary"
              onClick={save}
              loading={saving}
              disabled={!dirty}
            >
              Save policy
            </Button>
            {dirty && <Text type="secondary">Unsaved changes</Text>}
          </Space>
        </Space>
      )}
    </section>
  );
};

export default memo(SponsorshipPolicySection);
